import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  CircularProgress,
  TablePagination,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  DeleteSweep as DeleteSweepIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Upload as UploadIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';
import usePageTitle from '../../hooks/usePageTitle';

const SageMappings = () => {
  // State
  const [mappings, setMappings] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [mappingToEdit, setMappingToEdit] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFiles, setImportFiles] = useState([]);
  const [importValidation, setImportValidation] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importStep, setImportStep] = useState(0);
  
  // Bulk selection
  const [selectedMappings, setSelectedMappings] = useState([]);
  
  // Filter menu
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  
  // New mapping form
  const [newMapping, setNewMapping] = useState({
    external_code: '',
    internal_item: null,
    notes: ''
  });

  // For autocomplete input value
  const [itemInputValue, setItemInputValue] = useState('');

  usePageTitle(searchTerm ? `Sage Mappings: ${searchTerm}` : 'Sage Mappings');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reload when search, filters, or pagination change
  useEffect(() => {
    loadMappings();
  }, [debouncedSearch, sourceFilter, page, rowsPerPage]);

  const loadMappings = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
      };
      
      if (sourceFilter !== 'all') {
        params.source = sourceFilter;
      }
      
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      
      const response = await manufacturingAPI.productCodeMappings.list(params);
      
      if (response.data.results) {
        setMappings(response.data.results);
        setTotalCount(response.data.count || response.data.results.length);
      } else {
        setMappings(response.data);
        setTotalCount(response.data.length);
      }
    } catch (error) {
      toast.error('Failed to load mappings');
      console.error('Load mappings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchItems = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setItems([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await manufacturingAPI.searchItemsForBOM({ 
        search: searchTerm,
        exclude: []
      });
      
      let results = response.data.results || response.data;
      
      results.sort((a, b) => {
        const searchUpper = searchTerm.toUpperCase();
        const aCode = a.item_code.toUpperCase();
        const bCode = b.item_code.toUpperCase();
        
        const aEndsIn000 = aCode.endsWith('000');
        const bEndsIn000 = bCode.endsWith('000');
        if (aEndsIn000 && !bEndsIn000) return -1;
        if (!aEndsIn000 && bEndsIn000) return 1;
        
        if (aCode === searchUpper) return -1;
        if (bCode === searchUpper) return 1;
        
        const aStarts = aCode.startsWith(searchUpper);
        const bStarts = bCode.startsWith(searchUpper);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        
        return aCode.localeCompare(bCode);
      });
      
      setItems(results);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search items');
      setItems([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleExternalCodeChange = (value) => {
    const upperValue = value.toUpperCase().trim();
    setNewMapping(prev => ({ ...prev, external_code: value }));
    
    if (upperValue.length >= 3) {
      const matchingItem = items.find(item => 
        item.item_code.toUpperCase() === upperValue ||
        item.item_code.toUpperCase().includes(upperValue)
      );
      
      if (matchingItem) {
        setNewMapping(prev => ({ ...prev, internal_item: matchingItem }));
        setItemInputValue(matchingItem.item_code);
      }
    }
  };

  const handleDelete = (mapping) => {
    setMappingToDelete(mapping);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await manufacturingAPI.productCodeMappings.delete(mappingToDelete.id);
      toast.success('Mapping deleted');
      loadMappings();
    } catch (error) {
      toast.error('Failed to delete mapping');
    } finally {
      setDeleteDialogOpen(false);
      setMappingToDelete(null);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await manufacturingAPI.productCodeMappings.bulkDelete(selectedMappings);
      toast.success(`Deleted ${selectedMappings.length} mappings`);
      setSelectedMappings([]);
      loadMappings();
    } catch (error) {
      toast.error('Failed to delete mappings');
    } finally {
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleCreateMapping = async (keepOpen = false) => {
    if (!newMapping.external_code || !newMapping.internal_item) {
      toast.warning('Please fill in all required fields');
      return;
    }

    try {
      await manufacturingAPI.productCodeMappings.create({
        external_code: newMapping.external_code.trim().toUpperCase(),
        internal_item: newMapping.internal_item.id,
        source_system: 'sage',
        notes: newMapping.notes
      });
      
      toast.success(
        keepOpen 
          ? 'Mapping saved! Add another...' 
          : 'Mapping created successfully',
        { autoClose: 1500 }
      );
      
      setNewMapping({ external_code: '', internal_item: null, notes: '' });
      setItemInputValue('');
      
      loadMappings();
      
      if (keepOpen) {
        setTimeout(() => {
          const input = document.querySelector('input[name="sage-code-input"]');
          if (input) input.focus();
        }, 100);
      } else {
        setCreateDialogOpen(false);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('This mapping already exists or there was a validation error');
      } else {
        toast.error('Failed to create mapping');
      }
    }
  };

  const handleEditMapping = (mapping) => {
    setMappingToEdit({
      ...mapping,
      internal_item: items.find(item => item.id === mapping.internal_item) || {
        id: mapping.internal_item,
        item_code: mapping.internal_item_display?.split(' - ')[0] || '',
        name: mapping.internal_item_display?.split(' - ')[1] || ''
      }
    });
    setEditDialogOpen(true);
  };

  const handleUpdateMapping = async () => {
    try {
      await manufacturingAPI.productCodeMappings.update(mappingToEdit.id, {
        internal_item: mappingToEdit.internal_item.id,
        notes: mappingToEdit.notes
      });
      
      toast.success('Mapping updated successfully');
      setEditDialogOpen(false);
      setMappingToEdit(null);
      loadMappings();
    } catch (error) {
      toast.error('Failed to update mapping');
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedMappings(mappings.map(m => m.id));
    } else {
      setSelectedMappings([]);
    }
  };

  const handleSelectMapping = (id) => {
    setSelectedMappings(prev => {
      if (prev.includes(id)) {
        return prev.filter(mappingId => mappingId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleExportMappings = () => {
    if (mappings.length === 0) {
      toast.warning('No mappings to export');
      return;
    }

    const csvContent = [
      ['Sage Code', 'Internal Item Code', 'Internal Item Name', 'Notes', 'Created At'].join(','),
      ...mappings.map(m => [
        m.external_code,
        m.internal_item_display?.split(' - ')[0] || '',
        m.internal_item_display?.split(' - ')[1] || '',
        `"${(m.notes || '').replace(/"/g, '""')}"`,
        new Date(m.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sage_mappings_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    if (totalCount > mappings.length) {
      toast.info(`Exported ${mappings.length} of ${totalCount} mappings (current page only)`);
    } else {
      toast.success('Mappings exported to CSV');
    }
  };

  // Import functions
  const handleImportClick = () => {
    setImportDialogOpen(true);
    setImportStep(0);
    setImportFiles([]);
    setImportValidation(null);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ['xlsx', 'xls', 'csv'].includes(ext);
    });

    if (validFiles.length !== files.length) {
      toast.warning('Some files were skipped. Only Excel (.xlsx, .xls) and CSV files are supported.');
    }

    setImportFiles(prev => [...prev, ...validFiles]);
    event.target.value = '';
  };

  const removeFile = (index) => {
    setImportFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleValidateImport = async () => {
    if (importFiles.length === 0) {
      toast.warning('Please select at least one file');
      return;
    }

    setImportLoading(true);
    setImportStep(1);

    try {
      const allValidation = {
        files: [],
        totalRows: 0,
        validRows: 0,
        errors: [],
        warnings: [],
        missingItems: [],
        duplicateExternal: [],
        newMappings: [],
        existingMappings: [],
      };

      for (const file of importFiles) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await manufacturingAPI.productCodeMappings.validateImport(formData);
          const validation = response.data.validation;

          allValidation.files.push({
            name: file.name,
            ...validation
          });

          allValidation.totalRows += validation.total_rows || 0;
          allValidation.validRows += validation.valid_rows || 0;
          
          if (validation.errors) {
            allValidation.errors.push(...validation.errors.map(e => ({ ...e, file: file.name })));
          }
          if (validation.warnings) {
            allValidation.warnings.push(...validation.warnings.map(w => ({ ...w, file: file.name })));
          }
          if (validation.missing_items) {
            validation.missing_items.forEach(item => {
              if (!allValidation.missingItems.includes(item)) {
                allValidation.missingItems.push(item);
              }
            });
          }
          if (validation.duplicate_external_codes) {
            validation.duplicate_external_codes.forEach(code => {
              if (!allValidation.duplicateExternal.includes(code)) {
                allValidation.duplicateExternal.push(code);
              }
            });
          }
          if (validation.new_mappings) {
            allValidation.newMappings.push(...validation.new_mappings);
          }
          if (validation.existing_mappings) {
            allValidation.existingMappings.push(...validation.existing_mappings);
          }
        } catch (error) {
          allValidation.errors.push({
            file: file.name,
            row: 0,
            message: `Failed to process file: ${error.response?.data?.error || error.message}`
          });
        }
      }

      setImportValidation(allValidation);
      setImportStep(2);
      
      if (allValidation.errors.length > 0) {
        toast.error(`Validation found ${allValidation.errors.length} errors - fix them before importing`);
      } else if (allValidation.warnings.length > 0) {
        toast.info(`Validation complete with ${allValidation.warnings.length} warnings`);
      } else {
        toast.success('All files validated successfully!');
      }
    } catch (error) {
      toast.error('Failed to validate import files');
      console.error(error);
      setImportStep(0);
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importValidation || importValidation.errors.length > 0) {
      toast.error('Cannot import with validation errors. Please fix errors first.');
      return;
    }

    setImportLoading(true);
    setImportStep(3);

    try {
      let totalCreated = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;

      for (const file of importFiles) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await manufacturingAPI.productCodeMappings.importMappings(formData);
          totalCreated += response.data.created || 0;
          totalUpdated += response.data.updated || 0;
          totalSkipped += response.data.skipped || 0;
        } catch (error) {
          console.error(`Failed to import ${file.name}:`, error);
          toast.error(`Failed to import ${file.name}`);
        }
      }

      setImportStep(4);
      toast.success(`Import complete! Created: ${totalCreated}, Updated: ${totalUpdated}`);
      
      await loadMappings();
      
      setTimeout(() => {
        handleCloseImportDialog();
      }, 3000);
    } catch (error) {
      toast.error('Import failed');
      console.error(error);
      setImportStep(2);
    } finally {
      setImportLoading(false);
    }
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFiles([]);
    setImportValidation(null);
    setImportStep(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSelectedMappings([]);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setSelectedMappings([]);
  };

  const handleFilterChange = (filter) => {
    setSourceFilter(filter);
    setFilterAnchor(null);
    setPage(0);
    setSelectedMappings([]);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LinkIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Product Code Mappings</Typography>
            <Typography variant="body2" color="text.secondary">
              Map Sage product codes to your internal item codes
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadMappings}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleImportClick}
            color="secondary"
          >
            Import from Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add Mapping
          </Button>
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>What are code mappings?</strong> When Sage product codes differ from your internal item codes,
          mappings allow the system to automatically match them during imports. For example, Sage code "TBL-001-OLD"
          can map to your internal code "TABLE-001".
        </Typography>
      </Alert>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="caption">
                Total Mappings
              </Typography>
              <Typography variant="h4" color="primary">
                {totalCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="caption">
                Current Page
              </Typography>
              <Typography variant="h4" color="success.main">
                {mappings.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="caption">
                Search Filter
              </Typography>
              <Typography variant="h6" color="info.main" sx={{ wordBreak: 'break-all' }}>
                {debouncedSearch || 'None'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="caption">
                Selected
              </Typography>
              <Typography variant="h4" color="secondary.main">
                {selectedMappings.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper sx={{ p: 3 }}>
        {/* Toolbar */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search Sage code, internal code, or notes..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: loading && searchTerm ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
              sx={{ width: 350 }}
            />
            
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              size="small"
            >
              {sourceFilter === 'all' ? 'All Sources' : sourceFilter === 'sage' ? 'Sage 50' : 'Manual'}
            </Button>
            
            <Menu
              anchorEl={filterAnchor}
              open={Boolean(filterAnchor)}
              onClose={() => setFilterAnchor(null)}
            >
              <MenuItem onClick={() => handleFilterChange('all')}>
                <Chip label="All Sources" size="small" sx={{ mr: 1 }} />
              </MenuItem>
              <MenuItem onClick={() => handleFilterChange('sage')}>
                <Chip label="Sage 50" size="small" color="success" sx={{ mr: 1 }} />
              </MenuItem>
              <MenuItem onClick={() => handleFilterChange('manual')}>
                <Chip label="Manual" size="small" color="info" sx={{ mr: 1 }} />
              </MenuItem>
            </Menu>

            <Chip 
              label={`${totalCount} mappings found`}
              color="primary"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {selectedMappings.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteSweepIcon />}
                onClick={() => setBulkDeleteDialogOpen(true)}
              >
                Delete {selectedMappings.length}
              </Button>
            )}
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportMappings}
              disabled={mappings.length === 0}
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedMappings.length === mappings.length && mappings.length > 0}
                    indeterminate={selectedMappings.length > 0 && selectedMappings.length < mappings.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell width="25%">Sage Code (External)</TableCell>
                <TableCell width="5%" align="center">-></TableCell>
                <TableCell width="35%">Internal Item</TableCell>
                <TableCell width="15%">Source</TableCell>
                <TableCell width="10%">Created</TableCell>
                <TableCell width="10%" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Loading mappings...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <LinkIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm || sourceFilter !== 'all' 
                        ? 'No mappings found matching your filters' 
                        : 'No mappings yet'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {!searchTerm && sourceFilter === 'all' 
                        ? 'Create mappings manually or they will be created automatically during Sage imports'
                        : 'Try adjusting your search or filters'}
                    </Typography>
                    {!searchTerm && sourceFilter === 'all' && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={{ mt: 2 }}
                      >
                        Create First Mapping
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((mapping) => (
                  <TableRow 
                    key={mapping.id} 
                    hover
                    selected={selectedMappings.includes(mapping.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedMappings.includes(mapping.id)}
                        onChange={() => handleSelectMapping(mapping.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {mapping.external_code}
                      </Typography>
                      {mapping.notes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {mapping.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <LinkIcon color="action" fontSize="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {mapping.internal_item_display}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={mapping.source_system === 'sage' ? 'Sage 50' : 'Manual'} 
                        size="small"
                        color={mapping.source_system === 'sage' ? 'success' : 'info'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(mapping.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit mapping">
                        <IconButton
                          size="small"
                          onClick={() => handleEditMapping(mapping)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete mapping">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(mapping)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Import Dialog */}
      <Dialog 
        open={importDialogOpen} 
        onClose={importStep < 3 ? handleCloseImportDialog : undefined}
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown={importStep === 3}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UploadIcon />
              Import Code Mappings from Excel/CSV
            </Box>
            {importStep < 3 && (
              <IconButton size="small" onClick={handleCloseImportDialog}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Stepper */}
            <Stepper activeStep={importStep} sx={{ mb: 4 }}>
              <Step>
                <StepLabel>Select Files</StepLabel>
              </Step>
              <Step>
                <StepLabel>Validate</StepLabel>
              </Step>
              <Step>
                <StepLabel>Review</StepLabel>
              </Step>
              <Step>
                <StepLabel>Import</StepLabel>
              </Step>
            </Stepper>

            {/* Step 0: Upload Files */}
            {importStep === 0 && (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Expected file format:</strong> Excel (.xlsx, .xls) or CSV with these exact columns:
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    - <strong>SAGE SKU</strong> (required) - The code from Sage system
                    <br />
                    - <strong>SAP SKU</strong> (required) - Your internal item code
                    <br />
                    - <strong>Item Description</strong> (optional) - Product description
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Example: SAGE SKU "TBL-OLD-001" maps to SAP SKU "TABLE-000"
                  </Typography>
                </Alert>

                <Paper
                  sx={{
                    p: 4,
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    bgcolor: 'background.default',
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => document.getElementById('import-file-input').click()}
                >
                  <input
                    id="import-file-input"
                    type="file"
                    multiple
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Click to select files or drag and drop
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Excel (.xlsx, .xls) or CSV files
                  </Typography>
                </Paper>

                {importFiles.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Files ({importFiles.length}):
                    </Typography>
                    <List dense>
                      {importFiles.map((file, index) => (
                        <ListItem
                          key={index}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => removeFile(index)}>
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>
                            <FileIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={file.name}
                            secondary={`${(file.size / 1024).toFixed(2)} KB`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}

            {/* Step 1: Validating */}
            {importStep === 1 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Validating Files...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Checking for errors and missing items
                </Typography>
                <LinearProgress sx={{ mt: 2 }} />
              </Box>
            )}

            {/* Step 2: Validation Results */}
            {importStep === 2 && importValidation && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Total Rows
                        </Typography>
                        <Typography variant="h5">
                          {importValidation.totalRows}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Valid
                        </Typography>
                        <Typography variant="h5" color="success.main">
                          {importValidation.validRows}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Errors
                        </Typography>
                        <Typography variant="h5" color="error.main">
                          {importValidation.errors.length}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Warnings
                        </Typography>
                        <Typography variant="h5" color="warning.main">
                          {importValidation.warnings.length}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Errors */}
                {importValidation.errors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {importValidation.errors.length} Errors Found - Import Blocked
                    </Typography>
                    <List dense>
                      {importValidation.errors.slice(0, 10).map((error, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <ErrorIcon color="error" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Row ${error.row}: ${error.message}`}
                            secondary={error.file ? `File: ${error.file}` : undefined}
                          />
                        </ListItem>
                      ))}
                      {importValidation.errors.length > 10 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 7 }}>
                          ... and {importValidation.errors.length - 10} more errors
                        </Typography>
                      )}
                    </List>
                  </Alert>
                )}

                {/* Missing Items */}
                {importValidation.missingItems.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Missing Items ({importValidation.missingItems.length})
                    </Typography>
                    <Typography variant="body2">
                      The following internal item codes do not exist in your system:
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {importValidation.missingItems.slice(0, 20).map((item, idx) => (
                        <Chip key={idx} label={item} size="small" color="error" variant="outlined" />
                      ))}
                      {importValidation.missingItems.length > 20 && (
                        <Chip label={`+${importValidation.missingItems.length - 20} more`} size="small" />
                      )}
                    </Box>
                  </Alert>
                )}

                {/* Warnings */}
                {importValidation.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {importValidation.warnings.length} Warnings
                    </Typography>
                    <List dense>
                      {importValidation.warnings.slice(0, 5).map((warning, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <WarningIcon color="warning" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Row ${warning.row}: ${warning.message}`}
                            secondary={warning.file ? `File: ${warning.file}` : undefined}
                          />
                        </ListItem>
                      ))}
                      {importValidation.warnings.length > 5 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 7 }}>
                          ... and {importValidation.warnings.length - 5} more warnings
                        </Typography>
                      )}
                    </List>
                  </Alert>
                )}

                {/* Success Summary */}
                {importValidation.errors.length === 0 && (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    <Typography variant="subtitle2" gutterBottom>
                      Validation Passed!
                    </Typography>
                    <Typography variant="body2">
                      Ready to import {importValidation.validRows} mappings
                      {importValidation.newMappings.filter(m => m.action === 'create').length > 0 && 
                        ` (${importValidation.newMappings.filter(m => m.action === 'create').length} new)`}
                      {importValidation.newMappings.filter(m => m.action === 'update').length > 0 && 
                        `, ${importValidation.newMappings.filter(m => m.action === 'update').length} updates`}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {/* Step 3: Importing */}
            {importStep === 3 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Importing Mappings...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we import your data
                </Typography>
                <LinearProgress sx={{ mt: 2 }} />
              </Box>
            )}

            {/* Step 4: Complete */}
            {importStep === 4 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom color="success.main">
                  Import Complete!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  All mappings have been imported successfully
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {importStep === 0 && (
            <>
              <Button onClick={handleCloseImportDialog}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleValidateImport}
                disabled={importFiles.length === 0}
                startIcon={<CheckIcon />}
              >
                Validate Files
              </Button>
            </>
          )}

          {importStep === 2 && (
            <>
              <Button onClick={() => setImportStep(0)}>
                Back
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={handleCloseImportDialog}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmImport}
                disabled={importValidation.errors.length > 0}
                startIcon={<UploadIcon />}
                color="success"
              >
                Import {importValidation.validRows} Mappings
              </Button>
            </>
          )}

          {importStep === 4 && (
            <Button variant="contained" onClick={handleCloseImportDialog}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Mapping Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => {
          setCreateDialogOpen(false);
          setNewMapping({ external_code: '', internal_item: null, notes: '' });
          setItemInputValue('');
        }}
        maxWidth="md" 
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            if (newMapping.external_code && newMapping.internal_item) {
              handleCreateMapping(true);
            }
          }
        }}
      >
        <style>
          {`
            kbd {
              background-color: #f0f0f0;
              border: 1px solid #ccc;
              border-radius: 3px;
              padding: 2px 6px;
              font-family: monospace;
              font-size: 0.9em;
              box-shadow: 0 1px 2px rgba(0,0,0,0.1);
            }
          `}
        </style>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon />
              Quick Code Mapping Entry
            </Box>
            <Chip 
              label={`${totalCount} total mappings`} 
              size="small" 
              color="primary"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Quick Entry:</strong> Type Sage code, Tab to internal item, Type to search, Press <kbd>Ctrl+Enter</kbd> to save and continue!
              </Typography>
            </Alert>

            <TextField
              autoFocus
              name="sage-code-input"
              label="Sage Code (External)"
              placeholder="Type Sage code..."
              value={newMapping.external_code}
              onChange={(e) => handleExternalCodeChange(e.target.value)}
              fullWidth
              required
              helperText={
                newMapping.internal_item && newMapping.external_code.length >= 3
                  ? `Auto-matched: ${newMapping.internal_item.item_code}`
                  : "Press Tab to move to internal item"
              }
              InputProps={{
                endAdornment: newMapping.internal_item && newMapping.external_code.length >= 3 ? (
                  <InputAdornment position="end">
                    <CheckIcon color="success" />
                  </InputAdornment>
                ) : null
              }}
            />

            <Autocomplete
              id="internal-item-autocomplete"
              options={items}
              getOptionLabel={(option) => option.item_code}
              value={newMapping.internal_item}
              inputValue={itemInputValue}
              onInputChange={(event, newInputValue) => {
                setItemInputValue(newInputValue);
                if (newInputValue.length >= 2) {
                  searchItems(newInputValue);
                } else if (newInputValue.length === 0) {
                  setItems([]);
                }
              }}
              onChange={(e, newValue) => {
                setNewMapping({ ...newMapping, internal_item: newValue });
                if (newValue) {
                  setItemInputValue(newValue.item_code);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Internal Item Code"
                  placeholder="Start typing item code..."
                  required
                  helperText={
                    newMapping.internal_item 
                      ? `${newMapping.internal_item.item_code} - ${newMapping.internal_item.name}`
                      : itemInputValue && items.length === 0 && itemInputValue.length >= 2
                      ? `No items found matching "${itemInputValue}"`
                      : "Type at least 2 characters to search"
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                        {option.item_code}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {option.name}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText={
                itemInputValue && itemInputValue.length < 2
                  ? "Type at least 2 characters to search" 
                  : itemInputValue 
                  ? `No items found matching "${itemInputValue}"` 
                  : "Start typing to search..."
              }
              selectOnFocus
              clearOnBlur={false}
              handleHomeEndKeys
              autoHighlight
              loading={searchLoading}
              filterOptions={(x) => x}
            />

            <TextField
              label="Notes (Optional)"
              placeholder="Add notes if needed..."
              value={newMapping.notes}
              onChange={(e) => setNewMapping({ ...newMapping, notes: e.target.value })}
              fullWidth
              size="small"
              helperText="Optional - press Tab to skip"
            />

            {newMapping.external_code && newMapping.internal_item && (
              <Alert severity="success" icon={<CheckIcon />}>
                <Typography variant="body2" fontWeight={600}>
                  Ready to Save - Press <kbd>Ctrl+Enter</kbd>
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mt: 1, fontSize: '1.1rem' }}>
                  {newMapping.external_code.toUpperCase()} -&gt; {newMapping.internal_item.item_code}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {newMapping.internal_item.name}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setCreateDialogOpen(false);
              setNewMapping({ external_code: '', internal_item: null, notes: '' });
              setItemInputValue('');
            }}
          >
            Close
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            onClick={() => handleCreateMapping(true)}
            disabled={!newMapping.external_code || !newMapping.internal_item}
            startIcon={<AddIcon />}
          >
            Save and Add Another
          </Button>
          <Button
            variant="contained"
            onClick={() => handleCreateMapping(false)}
            disabled={!newMapping.external_code || !newMapping.internal_item}
            startIcon={<CheckIcon />}
          >
            Save and Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Mapping Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            Edit Code Mapping
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>External Code:</strong> {mappingToEdit?.external_code}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                You can only change which internal item this maps to. To change the external code, delete this mapping and create a new one.
              </Typography>
            </Alert>

            <Autocomplete
              options={items}
              getOptionLabel={(option) => `${option.item_code} - ${option.name}`}
              value={mappingToEdit?.internal_item || null}
              onInputChange={(event, newInputValue) => {
                if (newInputValue.length >= 2) {
                  searchItems(newInputValue);
                }
              }}
              onChange={(e, newValue) => setMappingToEdit({ ...mappingToEdit, internal_item: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Internal Item"
                  required
                  placeholder="Search items..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              loading={searchLoading}
              filterOptions={(x) => x}
            />

            <TextField
              label="Notes"
              value={mappingToEdit?.notes || ''}
              onChange={(e) => setMappingToEdit({ ...mappingToEdit, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateMapping}
            disabled={!mappingToEdit?.internal_item}
            startIcon={<CheckIcon />}
          >
            Update Mapping
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Mapping?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this mapping?
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              <strong>{mappingToDelete?.external_code}</strong> -&gt; {mappingToDelete?.internal_item_display}
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Future Sage imports will not recognize this code until you map it again.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Delete {selectedMappings.length} Mappings?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You are about to delete <strong>{selectedMappings.length}</strong> code mappings.
              This action cannot be undone.
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Future Sage imports will not recognize these codes until you map them again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleBulkDelete} 
            color="error" 
            variant="contained"
            startIcon={<DeleteSweepIcon />}
          >
            Delete All Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SageMappings;